/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { AppGrantsSection } from './AppGrantsSection'
import { InsightCategoriesSection } from './InsightCategoriesSection'
import type { PermissionsCap, PermissionsValue } from './types'
import type { UsageCount, UsageIndex } from './usage'

export interface PermissionsEditorProps {
  value: PermissionsValue
  onChange: (next: PermissionsValue) => void
  /** The company's profile; null when there is no company or it caps nothing. */
  cap: PermissionsCap | null
  /** Who has what on a dashboard (drives the badges and the save confirmation). */
  usage: UsageIndex
  /** Badge text for a usage count; defaults to a plain "In Use". */
  inUseLabel?: (count: UsageCount) => string
  disabled?: boolean
  /** Page-specific controls rendered above the apps (company, access, support). */
  header?: React.ReactNode
  /** When set, rendered in place of the app and insight sections (the header still shows). */
  sectionsReplacedBy?: React.ReactNode
}

const defaultInUseLabel = () => 'In Use'

/**
 * The apps + insights checkbox editor shared by the user, company and bulk pages.
 * It owns no save/cancel state — pages do, through usePermissionsForm.
 */
export const PermissionsEditor: React.FC<PermissionsEditorProps> = ({
  value,
  onChange,
  cap,
  usage,
  inUseLabel = defaultInUseLabel,
  disabled = false,
  header,
  sectionsReplacedBy,
}) => (
  <div data-testid="permissions-editor">
    {header}
    {sectionsReplacedBy ?? (
      <>
        <AppGrantsSection
          grants={value.grants}
          onChange={(grants) => onChange({ ...value, grants })}
          cap={cap}
          usage={usage}
          inUseLabel={inUseLabel}
          disabled={disabled}
        />
        <InsightCategoriesSection
          insights={value.insights}
          onChange={(insights) => onChange({ ...value, insights })}
          cap={cap}
          usage={usage}
          inUseLabel={inUseLabel}
          disabled={disabled}
        />
      </>
    )}
  </div>
)

export default PermissionsEditor
